import { IAssignEntity } from "../basics/basics.models";
import { EntityTypes, IEntityTypeIdentifier } from "@rightship-tech/common-ui/models/basic.models";
import { BaseValidationResponse } from "../validations/validations.models";
import { IInspectionStateEntity } from "../rightship-inspections/rightship-inspections.models";
import * as moment from "moment";
import { DataType, DataGridColumn } from "../queries/queries.models";
import { HistoryHelper } from "../../helpers/history-helper";
import { TasksConstants } from "../tasks/task.model";
import { ISubtaskEntity } from "../vessels/vessel.models";
import { UserClaimsService } from "../../services/authentication/user-claims.service";
import { Claims } from "../../constants/authentication/claims";
import { AttachmentType } from "../attachment/attachment.models";


export interface PscInspectionModel extends IAssignEntity {
    pscInspectionId: number;
    vesselId: number;
    information?: string;
    pscAuthorityId: number;
    pscInspectionSourceId?: number;
    pscInspectionTypeId?: number;
    portId?: number;
    legacyMarpol?: boolean;
    inspectionDate: Date;
    followUpInspection?: boolean;
    numberOfDaysDetained?: number;
    closeOutComments?: string;
    closed: boolean;
    shipDetained?: boolean;
    shipDetainedOverride?: boolean;
    knowDeficiencyDetails?: boolean;
    anyDeficiencies?: boolean;
    pscReasonForCloseOutId?: number;
    numberOfDeficiencies?: number;
    numberOfDeficienciesOverride?: number;
    releaseDate?: Date;
    pscInspectionDeficiencies?: PscDeficiencyModel[];
    rootCauseAnalysisResponseRatingId?: number;
    correctiveActionResponseRatingId?: number;
    preventiveActionResponseRatingId?: number;
    currentPscInspectionStatusHistory?: PscInspectionStatusHistory;
}

export interface PscInspectionStatusHistory {
    pscInspectionStatusHistoryId: number;
    pscInspectionStatusId: number;
    pscInspection: PscInspectionModel;
    statusSnippet: string;
}

export interface PscDeficiencyModel {
    pscInspectionId?: number;
    pscInspectionDeficiencyId?: number;
    pscDeficiencyAction1Id: number;
    closeOutPrevActionPreventReoccurFleet?: boolean;
    closeOutPrevActionPreventReoccurVessel?: boolean;
    closeOutDeficiencySeverityId?: number;
    pscDeficiencyId?: number;
    pscDeficiencyCategoryId?: number;
    pscDeficiencyCategoryFromDeficiencyId?: number;
    pscDeficiencyRiskAreaId?: number;
    manualDefectCode?: string;
    manualDefectText?: string;
    isActive: boolean;
    knownDeficiencyCode: boolean;
    immediateCause?: string;
    rootCause?: string;
    correctiveActions?: string;
    preventativeActions?: string;
}

export interface DataSubmissionClientModel extends IEntityTypeIdentifier, ISubtaskEntity {
    vesselApplicationFormId: number;
    correspondenceCount: number;
    pluginEntityTypeId: number;
    vesselId: number;
    defaultOnboardingRequestId: number;
    onboardingRequests: any[];
}

export interface PscClientModel extends IEntityTypeIdentifier, IInspectionStateEntity {
    closeoutSubtaskId?: number;
    closeoutSubtaskStatusId?: number;
    pscInspectionStatusId: number;
    pscInspectionStatus: string;
    vesselDateOfBuild: Date;
    vesselDwt: number;
    pscInspectionId: number;
    assignedUserId?: number;
    assignedTo?: string;
    vesselId: number;
    vesselName?: string;
    vesselImo?: string;
    vehicleIdentificationNumber?: string;
    europeanNumberOfIdentification?: string;
    shipManagerEmail?: string;
    vesselType?: string;
    inspectionDate: Date;
    inspectionDateString?: string;
    followUpInspection?: boolean;
    numberOfDaysDetained?: number;
    shipDetained?: boolean;
    shipDetainedOverride?: boolean;
    deficiencies?: PscDeficiencyClientModel[];
    statuses?: PscDeficiencyStatusClientModel[];
    pscAuthority: string;
    closed: boolean;
    port: string;
    country?: string;
    numberOfDeficiencies: number;
    numberOfDeficienciesOverride?: number;
    pscReasonForCloseOut: string;
    starRating: number;
    rating: string;
    rootCauseAnalysisResponseRatingId?: number;
    correctiveActionResponseRatingId?: number;
    preventiveActionResponseRatingId?: number;
    legacyMarpol?: boolean;
    documentsCount?: number;
    privateNotesCount?: number;
    correspondenceCount?: number;
    canActivateOrDeactivateSafetyScore?: boolean;
    misreportedReason: string;
    dataSources: DataSourcesVm[];
    hasDataSourceConflicts?: boolean;
    usedInSafetyScore?: boolean;
    hasUseInSafetyScoreDsChange?: boolean;
    hasUseInDetentionSafetyScoreDsChange?: boolean;
    concatenatedDataSources?: string;
    isInconsistentState: boolean;
    aiExtractionCount: number;
    showDeficiencyExtraInfo?: boolean;
    scoringCategoryDefects?: number;
    previousScoringCategoryDefects?: number;
    enabledNewUI?: boolean;
}
export interface DataSourcesVm {
    dataSource: string;
    dataSourceId: number;
    hasRfiAPI: boolean;
    platformCode: string;
}
export interface PscDeficiencyStatusClientModel {
    name: string;
    statusId: number;
    subtaskTypes?: number[];
    comment?: string;
    user: string;
    date: Date;
    previousStatusId?: number;
}

export interface PscDeficiencyClientModel {
    pscDeficiencyCategoryId?: number;
    pscDeficiencyId: number;
    deficiency: string;
    code: string;
    group: string;
    actionId?: number;
    severityId?: number;
    action: string;
    actionCode: string;
    immediateCause?: string;
    rootCause?: string;
    correctiveActions?: string;
    preventativeActions?: string;
    pscInspectionDeficiencyStatus: string;
    pscInspectionDeficiencyStatusId: number;
    riskArea: string;
    pscInspectionDeficiencyId: number;
    deficiencyDetails: string;
    actualDeficiencyDetails?: string;
    severityRemarks?: string;
    aiData: PscDeficiencyClientModel;
    hasCustomerCloseOutUpdatedUiContent: boolean;
    hasSeverityScoringUpdatedUiContent: boolean;
    hasUpdatedUiContent?: boolean;
    status: string;
    statusClassName: string;
    severity: string;
    originalAdminSeverity?: string;
    originalSeverity?: string;
    originalSeverityId?: number;
    beforeUpdateSeverityId?: number;
    thumbnails: ThumbnailClientModel[];
    aiInspectionDeficiencyId: string;
    knownDeficiencyCode: boolean;
    isNew: boolean;
    isManuallyAdded: boolean;
    element?: HTMLElement;
}

export interface ThumbnailClientModel {
    thumbnailFileName: string;
    originalAttachmentTypeName?: string;
    originalAttachmentName?: string;
    fileName: string;
    pageNumber: number;
    src?: string;
    sasUrl?: string;
    id?: string;
    attachmentType?: AttachmentType;
}

export enum DataSubmissionStepEnum {
    OnboardingWorkflow = 1,
    OnboardingCorrespondence = 2
}

export enum PscStepEnum {
    PscOverview = 1,
    PscDeficiencies = 2,
    PscDocuments = 3,
    PscPrivateNotes = 4,
    PscCorrespondence = 5,
    PscHistoryTrail = 6
}

export enum PscSeverityEnum {
    High = 1,
    Medium = 2,
    Low = 3,
    NotActive = 4
}

export enum PscFilters {

    // Default Filters
    PendingSubtask = 1,
    PendingVet = 2,
    VettedLast12Mths = 3,

    // Status Filters
    Created = 4,
    InProgress = 8,
    AwaitingResponse = 16,
    InspectionReassignment = 32,
    Closed = 64,
    PendingReview = 128,
    Misreported = 256,
    Discrepancies = 512,
    NotInSafetyScore = 1024,

    Statuses = Created | InProgress | AwaitingResponse | InspectionReassignment | Closed | PendingReview | Misreported,
    PendingDueDiligence = 100
}

export interface SavePscResponse extends BaseValidationResponse {
    linkedVetIds?: number[];
    generatedId?: number;
    pscAlreadyExists?: boolean;
    existentPscId?: number;
}

export class PscDataSourceAbbreviationMappings {
    public static nyk: string = "NIBIKI";
    public static ihs: string = "S&P Global";
    public static rsm: string = "RS subscriber";
    public static pub: string = "Non RS subscriber";
    public static rst: string = "Rightship Test";
    public static rsds: string = "Rightship Data Submission";
}

export class PscActivitiesColumns {
    public static getColumns(openInANewTab: boolean, rightShipUser: boolean, isCustomer: boolean, userClaimsService: UserClaimsService) {
        const columns: DataGridColumn[] = [];
        const newTab = openInANewTab ? 'target="_blank"' : '';
        columns.push(
            <DataGridColumn>{
                columnOrder: 1,
                sortOrder: 2,
                dataType: DataType.CustomTemplate,
                name: 'vesselName',
                title: "Name",
                value: '<div class="column">' +
                    '<div class="row align-items-middle">' +
                    '<a class="link" href="/psc/${item.pscInspectionId}" ' + newTab + ' mousedown.trigger="itemClick()">${item.vesselName}</a>' +
                    '<svg class="rs-badge" viewBox="0 0 30 30" if.bind="item.isRightShipMember && isRightShipUser">' +
                    '<g>' +
                    '<path fill="#003745" d="M2,0h26c1.1,0,2,0.9,2,2v26c0,1.1-0.9,2-2,2H2c-1.1,0-2-0.9-2-2V2C0,0.9,0.9,0,2,0z"/>' +
                    '<path fill="#FF5F1B" d="M2,0h1.5c0.5,0,1,0.2,1.4,0.6l15.5,14.8l0,0L4.9,29.5C4.5,29.8,4,30,3.5,30H2c-1.1,0-2-0.9-2-2V2C0,0.9,0.9,0,2,0z"/>' +
                    '</g>' +
                    '</svg>' +
                    '</div>' +
                    '<span if.bind="item.vesselImo">IMO ${item.vesselImo}</span>' +
                    '<span if.bind="item.vehicleIdentificationNumber">VIN ${item.vehicleIdentificationNumber}</span>' +
                    '<span if.bind="item.europeanNumberOfIdentification">ENI ${item.europeanNumberOfIdentification}</span></div>',
                instancePerRow: true,
                isSortable: true,
                parameter: {
                    item: null,
                    itemClick: () => {
                        HistoryHelper.addState(TasksConstants.historyTaskTabKey, TasksConstants.PSCListName);
                        return true;
                    },
                    isRightShipUser: rightShipUser
                }
            },
            <DataGridColumn>{
                columnOrder: 2,
                sortOrder: 6,
                dataType: DataType.DateTime,
                name: 'inspectionDate',
                title: 'Date of Inspection',
                isSortable: true,
                valueConverter: (value) => moment(value).format("DD MMM YYYY")
            },
            <DataGridColumn>{
                columnOrder: 3,
                sortOrder: 3,
                dataType: DataType.CustomTemplate,
                isSortable: false,
                name: 'information',
                title: 'Information',
                instancePerRow: true,
                disableSorting: true,
                value:
                    `<div>
                        <span>\${item.portName? item.portName: 'Unknown Port'} (\${item.pscAuthority})</span>
                        <span if.bind="item.followUpInspection">Fol: Y</span>
                        <span if.bind="!item.followUpInspection" style="color: \${item.numberOfDeficiencies > 0 && item.shipDetained? '#E61E2F': (item.numberOfDeficiencies > 0 && !item.shipDetained? '#C78800': '#008561')}">Def: \${item.numberOfDeficiencies? item.numberOfDeficiencies: 0}</span>\${!item.followUpInspection? ',&nbsp;': '' }
                        <span if.bind="!item.followUpInspection" style="color: \${item.shipDetained? '#E61E2F': ''}">Det: \${item.shipDetained? 'Y': 'N'}</span>
                    </div>`,
                parameter: {
                    item: null
                }
            },
            <DataGridColumn>{
                columnOrder: 5,
                sortOrder: 5,
                dataType: DataType.CustomTemplate,
                instancePerRow: true,
                isSortable: true,
                name: 'reviewer',
                title: 'Reviewer',
                value:
                    '<div class="reviewer" if.bind="item.reviewer">' +
                    '<span class="name">${getIsCleanAndIHS(item) ? `RS System` : item.reviewer}</span>' +
                    '<span class="type">${getIsCleanAndIHS(item) ? `Last Reviewer` : item.reviewerType}</span>' +
                    '</div>',
                parameter: {
                    item: null
                }
            },
            <DataGridColumn>{
                columnOrder: 6,
                sortOrder: 1,
                dataType: DataType.CustomTemplate,
                instancePerRow: true,
                isSortable: true,
                name: 'status',
                title: 'Status',
                value:
                    '<div class="status-badge-value row " if.bind="item.status">' +
                    '   <div>' +
                    '       <div class="row nowrap align-items-middle">' +
                    '       <div class="status-badge-dot">' +
                    '   </div>' +
                    '       <span>${getStatus(isCustomer, item)}</span>' +
                    '   </div>' +
                    '   <span class="status-date">${getDateToString(item.statusDate)}</span></div>' +
                    '   <div class="info" if.bind="item.statusSnippet && item.statusId != 6 && item.statusId != 4">' +
                    '       <i class="icon-info material-icons">info_outline</i>' +
                    '       <div class="status-tooltip">' +
                    '           <div class="wrap-tooltip">' +
                    '           <p class="desc">${item.statusSnippet}</p>' +
                    '       </div>' +
                    '   </div>' +
                    '</div>',
                getCellClass: (item: { entityTypeId: number, status: string, numberOfDeficiencies: number, shipDetained: boolean, concatenatedDataSources: string, statusId: number, pscInspectionStatusId: number }) => {
                    if ((this.getIsCleanPsc(item) && isCustomer) || (this.getIsCleanAndIHSPsc(item) && !isCustomer)) {
                        return `status-badge clean`;
                    }

                    return (item && item.status) ? `status-badge ${item.status.toLowerCase().replace(/[^A-z0-9]+/g, '-')}` : '';
                },
                parameter: {
                    item: null,
                    isCustomer: isCustomer,
                    getStatus: (isCustomer, item) => this.getStatus(isCustomer, item),
                    getDateToString: (date: Date | string) => this.getDateToString(date)
                }
            });


        if (userClaimsService.hasPermission(Claims.CanViewSourceColumnInTasksPSC)) {
            columns.push(
                <DataGridColumn>{
                    columnOrder: 7,
                    sortOrder: 7,
                    dataType: DataType.CustomTemplate,
                    instancePerRow: true,
                    isSortable: true,
                    name: 'concatenatedDataSources',
                    title: 'Source',
                    value:
                        '<div class="data-source row" if.bind="item.concatenatedDataSources">' +
                        '   <div>' +
                        '       <span>${item.concatenatedDataSources}</span>' +
                        '   </div>' +
                        '   <div class="info">' +
                        '       <i class="icon-info material-icons">info_outline</i>' +
                        '       <div class="data-source-tooltip">' +
                        '           <div class="wrap-tooltip">' +
                        '           <p class="desc">${getTooltipText(item.concatenatedDataSources)}</p>' +
                        '       </div>' +
                        '   </div>' +
                        '</div>',
                    parameter: {
                        item: null,
                        getTooltipText: (dataSources: string) => {
                            if (dataSources && dataSources.trim() != '') {
                                const list = dataSources.toLowerCase().trim().split("/");
                                return list.map(x => Object.keys(PscDataSourceAbbreviationMappings).indexOf(x) > -1 ? PscDataSourceAbbreviationMappings[x] : '').join("/");
                            }

                            return '';
                        }
                    }
                });
        }

        return columns;
    }

    public static getStatus(isCustomer: boolean, item: { entityTypeId: number, status: string, statusId: number, numberOfDeficiencies: number, shipDetained: boolean, concatenatedDataSources: string, pscInspectionStatusId: number }): string {
        if (!item) return null;

        if (this.getIsCleanAndIHSPsc(item) && !isCustomer) {
            return "Created";
        }

        if (isCustomer) {
            if (item.status == "Review Required") {
                return "Pending Review"
            }

            if (this.getIsCleanPsc(item)) {
                return "Clean";
            }
        }

        return item.status;
    }

    public static getDateToString(date: Date | string): string {
        if (!date)
            return 'N/A'

        let val: any = date;

        if (typeof date == 'string')
            val = new Date(date);

        if (val.getUTCMonth() == 0 && val.getUTCDay() == 1 && val.getUTCFullYear() == 1900)
            return 'N/A';

        return date ? moment(date).fromNow() : null;
    }

    public static getIsCleanPsc(item: { entityTypeId: number, uri?: string, numberOfDeficiencies: number, shipDetained: boolean, statusId: number, pscInspectionStatusId: number }): boolean {
        return item.numberOfDeficiencies === 0 && !item.shipDetained && (item.statusId === 1 || item.pscInspectionStatusId === 1) && (item.entityTypeId === EntityTypes.PSC || (item.uri && item.uri.toLowerCase() === "pscs"));
    }

    public static getIsCleanAndIHSPsc(item: { entityTypeId: number, numberOfDeficiencies: number, shipDetained: boolean, concatenatedDataSources: string, statusId: number, pscInspectionStatusId: number }): boolean {
        if (item && this.getIsCleanPsc(item) && item.concatenatedDataSources) {
            const dataSources = item.concatenatedDataSources.split("/");
            if (dataSources.length == 1) {
                return true;
            }

            if (dataSources.length == 2 && dataSources.find(x => x == "S&P Global")) {
                return true;
            }
        }

        return false;
    }
}

export interface OverridePscDetailsRequest {
    overridePscDetailsModel: OverridePscDetailsModel,
    attachments: File[]
}

export interface SavePscInspectionDeficiencyRequest {
    pscInspectionId: number,
    pscInspectionDeficiencyId: number,
    pscInspectionDeficiencyStatusId: number,
    severityScoringId: number;
    pscDeficiencyId: number;
    pscDeficiencyCategoryId: number;
    deficiencyDetails: string;
    actualDeficiencyDetails: string;
    pscDeficiencyActionId: number;
    immediateCause: string;
    rootCause: string;
    correctiveActions: string;
    preventativeActions: string;
    aiInspectionDeficiencyId: string;
    severityRemarks: string;
}

export interface PscInspectionDeficiencyActionResponse {
    isSuccess: boolean;
}

export interface OverridePscDetailsModel {
    pscId: number,
    isPscActiveInSafetyScore: boolean,
    isPscDetentionActiveInSafetyScore: boolean,
    justification: string,
    pscReasonForOverrideId: number,
    pscDetentionReasonForOverrideId: number
}

export interface OverridePscDetailsResponse extends BaseValidationResponse {
}

export enum PscInspectionDeficiencyStatusEnum {
    PendingReview = 1,
    ScoredAndReviewed = 2
}

export interface GetPscCloseOutDocumentLinksResponse {
    pscCloseOutDocumentLinks : PscCloseOutDocumentLink[];
}

export interface PscCloseOutDocumentLink {
    sasUrl: string;
    thumbnails: ThumbnailClientModel[];
}

export interface DeficiencySeverityChange {
    originalSeverity: string;
    revisedSeverity: string;
    count: number;
}
